import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FONTS, COLORS, BREAKPOINTS } from '../utils/styleConstants';
import { getPackages } from '../services/api';
import { formatAmountForDisplay } from '../utils/amountUtils';
import { useCredits } from '../context/CreditsContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import LoadingIndicator from '../components/LoadingIndicator';

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.8rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const Description = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    display: none;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  margin-bottom: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h2`
  color: ${COLORS.SECONDARY};
  font-size: 2.8rem;
  font-weight: 300;
  font-family: ${FONTS.PRIMARY};
  margin: 0 0 0 0;
  line-height: 1.2;

  @media (max-width: 992px) {
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
  }

  .bold {
    font-weight: 700;
  }
`;

const SubTitle = styled.h3`
  color: ${COLORS.SECONDARY};
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DisclaimerText = styled.p`
  color: #666;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.5;
  margin: 0;
  text-align: justify;

  strong {
    font-weight: 700;
    color: ${COLORS.SECONDARY};
  }

  &.light-blue {
    color: #86A9D5;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PackagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PackageTitle = styled.h3`
  color: #86A9D5;
  font-size: 3rem;
  font-weight: 700;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  letter-spacing: -1px;
`;

const PricePerUnit = styled.div`
  color: #86A9D5;
  font-size: 3rem;
  font-weight: 300;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
`;

const PriceSubtext = styled.div`
  color: #86A9D5;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  margin: -1rem 0 0.5rem 0;
  
  .discount {
    font-weight: 700;
  }
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #86A9D5;
  margin: 1rem 0;
`;

const PackageDescription = styled.div`
  color: #86A9D5;
  font-size: 1.1rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  margin: 0;
`;

const TotalPrice = styled.div`
  color: #86A9D5;
  font-size: 1.3rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  margin: 0;
`;

const PriceGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
`;

const PurchaseButton = styled.button`
  background: white;
  color: #86A9D5;
  border: 2px solid #86A9D5;
  border-radius: 25px;
  padding: 0.8rem 2.5rem;
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  min-width: 120px;

  &:hover {
    background: #86A9D5;
    color: white;
  }
`;

const PackageCard = styled.div`
  background: white;
  border: none;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    
    ${PackageTitle}, ${PricePerUnit}, ${PriceSubtext}, ${PackageDescription}, ${TotalPrice} {
      color: #1e3a5f;
    }
    
    ${Divider} {
      border-color: #1e3a5f;
    }
    
    ${PurchaseButton} {
      background: #1e3a5f;
      color: white;
      border-color: #1e3a5f;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: #fff2f2;
  color: #d32f2f;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #ffcdd2;
  margin-bottom: 1rem;
`;

const PurchasePage = () => {
  const navigate = useNavigate();
  const { formatCreditsDisplay } = useCredits();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch packages (required)
        const packagesResponse = await getPackages();
        setPackages(packagesResponse.packages || []);
        

        
      } catch (err) {
        setError('Error al cargar los paquetes de ofertas');
        console.error('Error fetching packages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePurchase = (packageData) => {
    // Navegar a la página de facturación con los datos del paquete
    navigate('/purchase/billing', { state: { packageData } });
  };

  const calculateDiscount = (packageData) => {
    if (packageData.credits === 1) return null;
    
    const singlePrice = packages.find(p => p.credits === 1)?.price || 15.9;
    const totalIfSingle = singlePrice * packageData.credits;
    const discount = Math.round(((totalIfSingle - packageData.price) / totalIfSingle) * 100);
    
    return discount > 0 ? discount : null;
  };

  const getPackageTitle = (packageData) => {
    switch (packageData.credits) {
      case 1: return 'OV1';
      case 10: return 'OV10';
      case 40: return 'OV40';
      default: return `OV${packageData.credits}`;
    }
  };



  if (loading) {
    return (
      <PageContainer>
        <Sidebar />
        <ContentArea>
          <LoadingIndicator />
        </ContentArea>
      </PageContainer>
    );
  }

  const creditsInfo = formatCreditsDisplay();

  return (
    <PageContainer>
      <Sidebar />
      <ContentArea>
        <FormHeader>
          <HeaderContent>
            <Title>Comprar Nuevas Ofertas</Title>
          </HeaderContent>
          <CreditsCounter>
            <CreditsNumber>{creditsInfo.number}</CreditsNumber>
            <CreditsLabel>{creditsInfo.label}</CreditsLabel>
          </CreditsCounter>
        </FormHeader>

        <MainContent>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ContentWrapper>
            <LeftSection>
              <SectionTitle>
                Elige<br/>
                <span className="bold">el paquete<br/>
                más adecuado</span><br/>
                a tus<br/>
                necesidades
              </SectionTitle>
              
              <div>
                <DisclaimerText>
                  <strong>* Cada Oferta cubre todo el proceso:</strong> desde el envío certificado de la oferta vinculante, la gestión de la contraoferta y su reformulación, hasta la firma certificada del acuerdo transaccional, en caso de alcanzarse. En ausencia de acuerdo, se incluyen los certificados de procedibilidad y de costas.
                </DisclaimerText>
                <DisclaimerText className="light-blue">
                  Los planes caducan pasados los 6 meses desde el momento del pago de los mismos y no se admiten reembolsos en el caso de no haber agotado los expedientes.
                </DisclaimerText>
              </div>
            </LeftSection>

            <RightSection>
              <PackagesContainer>
                {packages.map((packageData, index) => {
                  const discount = calculateDiscount(packageData);
                  
                  return (
                    <PackageCard key={packageData.id}>
                      <PackageTitle>{getPackageTitle(packageData)}</PackageTitle>
                      <PricePerUnit>{formatAmountForDisplay(packageData.price_per_credit)}</PricePerUnit>
                      <PriceSubtext>
                        por oferta*{discount ? <span className="discount"> (-{discount}%)</span> : ''}
                      </PriceSubtext>
                      
                      <Divider />
                      
                      <PriceGroup>
                        <PackageDescription>
                          {packageData.credits === 1 ? '1 Oferta' : `Pack ${packageData.credits} Ofertas`}
                        </PackageDescription>
                        <TotalPrice>
                          {formatAmountForDisplay(packageData.price)} <span style={{color: '#ccc', fontSize: '0.9rem'}}>(+IVA)</span>
                        </TotalPrice>
                      </PriceGroup>
                      
                      <PurchaseButton
                        onClick={() => handlePurchase(packageData)}
                      >
                        Comprar
                      </PurchaseButton>
                    </PackageCard>
                  );
                })}
              </PackagesContainer>
            </RightSection>
          </ContentWrapper>
        </MainContent>

        <Footer />
      </ContentArea>
    </PageContainer>
  );
};

export default PurchasePage; 